import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router';
import { apiFetch } from '../../services/api/client';
import { DEFAULT_ARTIST_ID } from '../../config/nova-constants';
import { toast } from 'react-toastify';
import { getAllUsers, getUserDetails, UserWithArtists } from '../../services/api/user.service';
import { Check, ArrowLeft } from 'lucide-react';

type ArtistOption = {
  artist_id: string;
  artist_name: string;
  image_url?: string;
  platform_artist_id?: string | null;
  ids_from: 'primary' | 'secondary';
  data_share?: boolean;
};

type UserOption = {
  id: string;
  label: string;
  email?: string;
  mobile?: string;
};

const MIN_QUERY = 3;

const ArtistMappingForm: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const hasUserQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.has('user');
  }, [location.search]);
  const [userId, setUserId] = useState('');
  const [userQuery, setUserQuery] = useState('');
  const [userOptions, setUserOptions] = useState<UserOption[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userSelected, setUserSelected] = useState(false);
  const [lastSelectedUserQuery, setLastSelectedUserQuery] = useState('');
  const userSkipRef = useRef(false);
  const userDirtyRef = useRef(false);
  const [artistQuery, setArtistQuery] = useState('');
  const [artistId, setArtistId] = useState('');
  const [artistOptions, setArtistOptions] = useState<ArtistOption[]>([]);
  const [loadingArtists, setLoadingArtists] = useState(false);
  const [artistSelected, setArtistSelected] = useState(false);
  const [lastSelectedArtistQuery, setLastSelectedArtistQuery] = useState('');
  const [artistSearchAttempted, setArtistSearchAttempted] = useState(false);
  const artistSkipRef = useRef(false);
  const artistDirtyRef = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const [sync, setSync] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Admin guard: systemRole must be "system_admin"
  const systemRole = useMemo(() => {
    try {
      return sessionStorage.getItem('systemRole');
    } catch {
      return null;
    }
  }, []);

  const handleArtistSearch = async (query: string) => {
    if (query.length < MIN_QUERY) {
      setArtistOptions([]);
      setArtistSearchAttempted(false);
      return;
    }
    setLoadingArtists(true);
    setError(null);
    try {
      const res: any = await apiFetch(`/artists/artists-list?search=${encodeURIComponent(query)}`);
      const list = Array.isArray(res?.data?.artists)
        ? res.data.artists
        : Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res)
            ? res
            : [];

      const normalized: ArtistOption[] = list.map((item: any) => ({
        artist_id: item.artist_id != null && item.artist_id !== '' ? String(item.artist_id) : (item.id != null && item.id !== '' ? String(item.id) : ''),
        artist_name: item.name || item.artist_name || 'Unknown artist',
        image_url: item.image_url || item.avatar || item.ARTIST_IMAGE || '',
        platform_artist_id: item.platform_artist_id || item.CM_ARTIST || null,
        ids_from: item.ids_from === 'secondary' ? 'secondary' : 'primary',
        data_share: item.data_share === true
      }));

      setArtistOptions(normalized);
      setArtistSearchAttempted(true);
    } catch (err: any) {
      setError(err?.message || 'Failed to fetch artists');
      setArtistOptions([]);
      setArtistSearchAttempted(true);
    } finally {
      setLoadingArtists(false);
    }
  };

  // Debounce artist search
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const incomingUserId = params.get('user');
    if (incomingUserId) {
      setUserId(incomingUserId);
      // Prefill display using user details
      getUserDetails(incomingUserId)
        .then((user: UserWithArtists) => {
          const artist = user.artists?.find(a => a.is_primary) || user.artists?.[0];
          const fullName = [user.firstname, user.lastname].filter(Boolean).join(' ').trim();
          const label = `${artist?.artist_name || fullName || user.email || 'User'}${user.email ? ` · ${user.email}` : ''}`;
          setUserQuery(label);
          setUserSelected(true);
          setLastSelectedUserQuery(label.trim());
          userSkipRef.current = true;
        })
        .catch(() => {});
    }
  }, [location.search]);

  useEffect(() => {
    const handle = setTimeout(() => {
      const trimmed = artistQuery.trim();
      if (!artistDirtyRef.current) {
        return;
      }
      if (artistSkipRef.current && trimmed === lastSelectedArtistQuery) {
        artistSkipRef.current = false;
        setArtistSelected(false);
        return;
      }
      if (artistSelected && trimmed === lastSelectedArtistQuery) {
        setArtistSelected(false);
        return;
      }
      handleArtistSearch(trimmed);
    }, 350);
    return () => clearTimeout(handle);
  }, [artistQuery, artistSelected, lastSelectedArtistQuery]);

  const handleUserSearch = async (query: string) => {
    if (query.length < MIN_QUERY) {
      setUserOptions([]);
      return;
    }
    setLoadingUsers(true);
    setError(null);
    try {
      const filters: any = {};
      if (query.includes('@')) {
        filters.email = query;
      } else if (/^\d+$/.test(query.replace(/\D/g, '')) && query.length >= 6) {
        filters.mobile = query;
      } else {
        filters.artist_name = query;
      }

      const response = await getAllUsers(1, 10, false, filters);
      const list = response.data || [];
      const normalized = list.map((u: UserWithArtists) => {
        const artist = u.artists?.find(a => a.is_primary) || u.artists?.[0];
        const fullName = [u.firstname, u.lastname].filter(Boolean).join(' ').trim();
        const label = `${artist?.artist_name || fullName || u.email || 'User'}${u.email ? ` · ${u.email}` : ''}`;
        return {
          id: String(u.id),
          label,
          email: u.email || '',
          mobile: u.mobile || ''
        } as UserOption;
      });
      setUserOptions(normalized);
    } catch (err: any) {
      setUserOptions([]);
      setError(err?.message || 'Failed to search users');
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    const handle = setTimeout(() => {
      const trimmed = userQuery.trim();
      if (!userSkipRef.current && !userDirtyRef.current) {
        return;
      }
      if (userSkipRef.current && trimmed === lastSelectedUserQuery) {
        userSkipRef.current = false;
        setUserSelected(false);
        return;
      }
      if (userSelected && trimmed === lastSelectedUserQuery) {
        setUserSelected(false);
        return;
      }
      handleUserSearch(trimmed);
    }, 350);
    return () => clearTimeout(handle);
  }, [userQuery, userSelected, lastSelectedUserQuery]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId.trim() || !artistId.trim()) {
      toast.error('User and Artist are required');
      return;
    }
    const selectedArtist = artistOptions.find(
      (a) => a.artist_id === artistId || a.platform_artist_id === artistId
    );
    const chartmetricId = selectedArtist?.platform_artist_id ?? null;
    const idsFrom = selectedArtist?.ids_from ?? 'primary';
    const isSecondary = idsFrom === 'secondary';

    setSubmitting(true);
    setError(null);
    try {
      await apiFetch('/artists/sync-artist-data', {
        method: 'POST',
        body: JSON.stringify({
          user_id: userId.trim(),
          artist_id: isSecondary ? (chartmetricId ?? artistId.trim()) : (artistId.trim() || DEFAULT_ARTIST_ID),
          sync,
          ids_from: idsFrom,
          artist_name: selectedArtist?.artist_name ?? '',
          ...(selectedArtist?.data_share != null && { data_share: selectedArtist.data_share }),
          ...(chartmetricId != null && chartmetricId !== '' && { chartmetric_id: chartmetricId }),
        }),
      });
      toast.success('Artist mapping synced successfully');
      setArtistQuery('');
      setArtistId('');
      setUserId('');
      setUserQuery('');
      setUserOptions([]);
      setUserSelected(false);
      setLastSelectedUserQuery('');
      setSync(false);
      setArtistOptions([]);
    } catch (err: any) {
      setError(err?.message || 'Failed to sync artist mapping');
      toast.error(err?.message || 'Failed to sync artist mapping');
    } finally {
      setSubmitting(false);
    }
  };

  if (systemRole !== "system_admin") {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm max-w-3xl w-full">
      {hasUserQuery && (
        <button
          type="button"
          onClick={() => navigate('/users-list')}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4 font-family-messina-sans"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Users List
        </button>
      )}
      <h1 className="text-xl font-semibold text-gray-900 mb-2">Artist Mapping</h1>
      <p className="text-sm text-gray-500 mb-6">Link a user to an artist and optionally trigger a sync.</p>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">User</label>
          <div className="relative">
            <input
              type="text"
              value={userQuery}
          onChange={(e) => {
            setUserQuery(e.target.value);
            setUserSelected(false);
            userDirtyRef.current = true;
            setLastSelectedUserQuery('');
            setUserId('');
          }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-800"
              placeholder="Search by email, mobile, or name (min 3 chars)"
            />
            {loadingUsers && (
              <div className="absolute right-3 top-2.5 h-4 w-4 border-2 border-gray-300 border-t-gray-800 rounded-full animate-spin" />
            )}
          </div>
          {userOptions.length > 0 && (
            <div className="mt-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg bg-white shadow">
              {userOptions.map((user) => (
                <button
                  type="button"
                  key={user.id}
                  onClick={() => {
                    // Keep input text unchanged to avoid re-triggering search
                    setUserId(user.id);
                    setUserSelected(true);
                    setLastSelectedUserQuery(userQuery.trim() || user.label.trim());
                    userSkipRef.current = true;
                    userDirtyRef.current = false;
                  }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                    userId === user.id ? 'bg-gray-100 font-semibold' : ''
                  }`}
                >
                  <div className="text-gray-900 flex items-center gap-2">
                    {user.label}
                    {userId === user.id && <Check size={14} className="text-green-600" />}
                  </div>
                  <div className="text-xs text-gray-500">
                    {user.email || user.mobile ? [user.email, user.mobile].filter(Boolean).join(' · ') : `ID: ${user.id}`}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Artist</label>
          <p className="text-xs text-gray-500 mb-1">
            Search by name, or paste a Spotify artist URL or ID
          </p>
          <div className="relative">
            <input
              type="text"
              value={artistQuery}
              onChange={(e) => {
                setArtistQuery(e.target.value);
                setArtistSelected(false);
                setLastSelectedArtistQuery('');
                artistDirtyRef.current = true;
                setArtistId('');
                setArtistSearchAttempted(false);
              }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-800"
              placeholder="Search artist name, Spotify URL, or ID (min 3 chars)"
            />
            {loadingArtists && (
              <div className="absolute right-3 top-2.5 h-4 w-4 border-2 border-gray-300 border-t-gray-800 rounded-full animate-spin" />
            )}
          </div>
          {!loadingArtists && artistSearchAttempted && artistOptions.length === 0 && !artistSelected && (
            <div className="mt-2 text-sm text-gray-500">Not found</div>
          )}
          {artistOptions.length > 0 && (
            <div className="mt-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg bg-white shadow">
              {artistOptions.map((artist, index) => {
                const isSecondaryOnly = artist.ids_from === 'secondary';
                const rowKey = artist.platform_artist_id || artist.artist_id || `idx-${index}`;
                const isSelected = isSecondaryOnly
                  ? artistId === (artist.platform_artist_id ?? '')
                  : artistId === artist.artist_id;
                return (
                  <button
                    type="button"
                    key={rowKey}
                    onClick={() => {
                      setArtistId(isSecondaryOnly ? (artist.platform_artist_id ?? '') : artist.artist_id);
                      setArtistSelected(true);
                      setLastSelectedArtistQuery(artistQuery.trim() || artist.artist_name.trim());
                      artistSkipRef.current = true;
                      artistDirtyRef.current = false;
                    }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                      isSelected ? 'bg-gray-100 font-semibold' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-gray-200 overflow-hidden flex items-center justify-center text-xs text-gray-600 border border-gray-100">
                        {artist.image_url && artist.image_url !== '-' ? (
                          <img
                            src={artist.image_url}
                            alt={artist.artist_name}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          artist.artist_name.slice(0, 2).toUpperCase()
                        )}
                      </div>
                      <div className="flex flex-col">
                        <div className="text-gray-900 flex items-center gap-2">
                          {artist.artist_name}
                          {isSelected && <Check size={14} className="text-green-600" />}
                        </div>
                        <div className="text-xs text-gray-500">
                          {isSecondaryOnly ? (
                            artist.platform_artist_id ? (
                              <>ChartMetric ID: <span className="font-semibold">{artist.platform_artist_id}</span></>
                            ) : (
                              <span className="text-gray-400">ChartMetric ID not available</span>
                            )
                          ) : (
                            <>
                              NOVA Artist ID: <span className="font-semibold">{artist.artist_id}</span>
                              {artist.platform_artist_id && (
                                <> · ChartMetric ID: <span className="font-semibold">{artist.platform_artist_id}</span></>
                              )}
                            </>
                          )}
                        </div>
                        {artist.data_share != null && (
                          <span
                            className={`inline-flex items-center mt-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded ${artist.data_share ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}
                            title={artist.data_share ? 'Artist exists in the data share' : 'This artist needs to be added to the data share'}
                          >
                            {artist.data_share ? 'Artist exists' : 'Need to add this artist to data share'}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          <input
            id="sync"
            type="checkbox"
            checked={sync}
            onChange={(e) => setSync(e.target.checked)}
            className="h-4 w-4 text-gray-800 border-gray-300 rounded"
          />
          <label htmlFor="sync" className="text-sm text-gray-700">
            Sync artist data after mapping
          </label>
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <button
          type="submit"
          disabled={submitting}
          className={`px-4 py-2 rounded-lg text-white text-sm font-medium ${
            submitting ? 'bg-gray-500 cursor-not-allowed' : 'bg-gray-900 hover:bg-gray-800'
          }`}
        >
          {submitting ? 'Saving...' : 'Save Mapping'}
        </button>
      </form>
    </div>
  );
};

export default ArtistMappingForm;
